import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Main site components
import Layout from './components/Layout/Layout';
import Home from './pages/Home';
import About from './pages/About';
import Admissions from './pages/Admissions';
import Contact from './pages/Contact';
import ApplicationForm from './pages/ApplicationForm';
import StudentPortal from './pages/StudentPortal';
import TeacherPortal from './pages/TeacherPortal';
import ParentPortal from './pages/ParentPortal';
import NotFound from './pages/NotFound';

// Admin components
import AdminLayout from './components/Layout/AdminLayout';
import AdminDashboard from './pages/Admin/AdminDashboard';
import FormConfigAdmin from './pages/Admin/FormConfigAdmin';
import FormBuilderPage from './pages/FormBuilderPage';
import UserManagement from './pages/Admin/UserManagement';
import ApplicationsAdmin from './pages/Admin/ApplicationsAdmin';
import StudentsAdmin from './pages/Admin/StudentsAdmin';
import RolesAdmin from './pages/Admin/RolesAdmin';
import StaffAdmin from './pages/Admin/StaffAdmin';
import CommunicationAdmin from './pages/Admin/CommunicationAdmin';
import EventsAdmin from './pages/Admin/EventsAdmin';
import FinanceAdmin from './pages/Admin/FinanceAdmin';
import AnalyticsAdmin from './pages/Admin/AnalyticsAdmin';
import SettingsAdmin from './pages/Admin/SettingsAdmin';
import SecurityAdmin from './pages/Admin/SecurityAdmin';
import WebsiteAdmin from './pages/Admin/WebsiteAdmin';
import SuperUserAdmin from './pages/Admin/SuperUserAdmin';
import ResourcesAdmin from './pages/Admin/ResourcesAdmin';

// Auth components
import Login from './pages/Auth/Login';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import ProtectedRoute from './components/Auth/ProtectedRoute';

const theme = createTheme({
  palette: {
    primary: {
      main: '#050979',
    },
    secondary: {
      main: '#dc004e',
    },
    success: {
      main: '#4caf50',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Main Site Routes */}
          <Route path="/" element={<Layout />}>
            <Route index element={<Home />} />
            <Route path="about" element={<About />} />
            <Route path="admissions" element={<Admissions />} />
            <Route path="contact" element={<Contact />} />
            <Route path="apply" element={<ApplicationForm />} />
            <Route path="student-portal" element={<StudentPortal />} />
            <Route path="teacher-portal" element={<TeacherPortal />} />
            <Route path="parent-portal" element={<ParentPortal />} />
          </Route>

          {/* Admin Routes - Protected */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="form-config" element={<FormConfigAdmin />} />
            <Route path="form-builder" element={<FormBuilderPage />} />

            {/* Application Management */}
            <Route path="applications" element={<ApplicationsAdmin />} />
            <Route path="applications/pending" element={<ApplicationsAdmin />} />
            <Route path="applications/approved" element={<ApplicationsAdmin />} />
            <Route path="applications/rejected" element={<ApplicationsAdmin />} />

            {/* Student Management */}
            <Route path="students" element={<StudentsAdmin />} />
            <Route path="students/enrollment" element={<StudentsAdmin />} />
            <Route path="students/records" element={<StudentsAdmin />} />

            {/* User Management */}
            <Route path="users" element={<UserManagement />} />
            <Route path="users/roles" element={<RolesAdmin />} />
            <Route path="users/staff" element={<StaffAdmin />} />

            {/* Communication */}
            <Route path="communication/templates" element={<CommunicationAdmin />} />
            <Route path="communication/notifications" element={<CommunicationAdmin />} />
            <Route path="communication/announcements" element={<CommunicationAdmin />} />

            {/* Events */}
            <Route path="events" element={<EventsAdmin />} />
            <Route path="events/calendar" element={<EventsAdmin />} />
            <Route path="events/categories" element={<EventsAdmin />} />

            {/* Finance */}
            <Route path="finance/tuition" element={<FinanceAdmin />} />
            <Route path="finance/payments" element={<FinanceAdmin />} />
            <Route path="finance/reports" element={<FinanceAdmin />} />

            {/* Analytics */}
            <Route path="analytics" element={<AnalyticsAdmin />} />
            <Route path="analytics/applications" element={<AnalyticsAdmin />} />
            <Route path="analytics/students" element={<AnalyticsAdmin />} />

            {/* System / Settings */}
            <Route path="settings" element={<SettingsAdmin />} />
            <Route path="data" element={<SettingsAdmin />} />
            <Route path="monitoring" element={<SettingsAdmin />} />

            {/* Security */}
            <Route path="security" element={<SecurityAdmin />} />
            <Route path="security/logs" element={<SecurityAdmin />} />
            <Route path="security/backup" element={<SecurityAdmin />} />

            {/* Website Management */}
            <Route path="website/pages" element={<WebsiteAdmin />} />
            <Route path="website/announcements" element={<WebsiteAdmin />} />
            <Route path="website/navigation" element={<WebsiteAdmin />} />
            <Route path="website/media" element={<WebsiteAdmin />} />

            {/* Resources */}
            <Route path="resources" element={<ResourcesAdmin />} />

            {/* Super User Console */}
            <Route path="superuser/data" element={<SuperUserAdmin />} />
            <Route path="superuser/bulk" element={<SuperUserAdmin />} />
            <Route path="superuser/audit" element={<SuperUserAdmin />} />
            <Route path="superuser/config" element={<SuperUserAdmin />} />
          </Route>

          {/* 404 Route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
